import { intl } from 'lang/intl'
import React, { CSSProperties, ReactElement } from 'react'
import { proxy, useSnapshot } from 'valtio'
import { v4 as uuid } from 'uuid'
import { TtIcon } from '@23/react'
import { logMessage } from '../../Logging/sentry'

import './Notices.scss'

type Notice = {
  id: string
  message: string
  content?: ReactElement
  cta?: {
    label: string
    link?: string
    onClick?: (n: Notice) => void
    dismissOnClick?: boolean
  }
  close: () => void
  expirationMillis?: number
  isClosable?: boolean
}

const noticesProxy = proxy<{
  queued: Notice[]
  showing: Notice[]
  isShown: boolean
}>({
  queued: [],
  showing: [],
  isShown: false,
})

const timeOuts: Record<Notice['id'], number> = {}

const MAX_SHOWN_NOTICES = 3

const updateShownNotices = () => {
  const noticesToAdd = Math.max(
    0,
    MAX_SHOWN_NOTICES - noticesProxy.showing.length
  )

  for (let i = 0; i < noticesToAdd; i += 1) {
    const notice = noticesProxy.queued.shift()

    if (!notice) break

    noticesProxy.showing.push(notice)

    if (notice.expirationMillis) {
      timeOuts[notice.id] = window.setTimeout(
        notice.close,
        notice.expirationMillis
      )
    }
  }
}

const showNotice = (notice: Omit<Notice, 'id' | 'close'>) => {
  const noticeId = uuid()

  logMessage(`User shown notice — ${notice.message}`, {
    level: 'info',
    extra: {
      ...notice,
    },
  })

  const closeNotice = () => {
    const timeOut = timeOuts[noticeId]
    if (timeOut) {
      window.clearTimeout(timeOut)
      delete timeOuts[noticeId]
    }

    const noticeShownIdx = noticesProxy.showing.findIndex(
      (n) => n.id === noticeId
    )
    if (noticeShownIdx !== -1) noticesProxy.showing.splice(noticeShownIdx, 1)

    const noticeQueuedIdx = noticesProxy.queued.findIndex(
      (n) => n.id === noticeId
    )
    if (noticeQueuedIdx !== -1) noticesProxy.queued.splice(noticeQueuedIdx, 1)

    updateShownNotices()
  }

  noticesProxy.queued.push({
    ...notice,
    id: noticeId,
    close: closeNotice,
  })
  updateShownNotices()

  return closeNotice
}

const Notices = () => {
  const notices = useSnapshot(noticesProxy)
  noticesProxy.isShown = true

  const handleClick = (notice: Notice) => {
    notice.cta?.onClick?.(notice)
    if (notice.cta?.dismissOnClick) notice.close()
  }

  const noticeQueueSize = notices.queued.length
  return (
    <div className="notices-list">
      {noticeQueueSize > 0 && (
        <div className="excess-notice-count">
          {noticeQueueSize === 1
            ? intl.formatMessage({ defaultMessage: '1 more notice' })
            : intl.formatMessage(
                { defaultMessage: '{count} more notices' },
                { count: noticeQueueSize }
              )}
        </div>
      )}
      {notices.showing.map((n, idx, arr) => (
        <div
          key={n.id}
          className="notice-item"
          role="alert"
          style={
            {
              '--idx': arr.length - idx + (noticeQueueSize > 0 ? 1 : 0) - 1,
            } as CSSProperties
          }
        >
          <div className="notice-item-header">
            <div className="notice-item-message">{n.message}</div>
            {n.cta && (
              <>
                {n.cta.link ? (
                  <a
                    className="notice-item-cta-anchor"
                    href={n.cta.link}
                    target="_blank"
                    rel="noreferrer"
                    onClick={() => {
                      // @ts-expect-error — TODO: fix
                      handleClick(n)
                      if (n.cta?.dismissOnClick) n.close()
                    }}
                  >
                    {n.cta.label}
                    <TtIcon library="one" name="external-link" />
                  </a>
                ) : (
                  <button
                    className="notice-item-cta-button"
                    type="button"
                    onClick={() => {
                      // @ts-expect-error — TODO: fix
                      handleClick(n)
                      if (n.cta?.dismissOnClick) n.close()
                    }}
                  >
                    {n.cta.label}
                  </button>
                )}
              </>
            )}
            {n.isClosable && (
              <>
                <div className="notice-item-separator" />
                <button
                  type="button"
                  className="notice-item-close"
                  onClick={n.close}
                >
                  <TtIcon name="close" library="one" />
                </button>
              </>
            )}
          </div>
          <div className="notice-item-content">{n.content}</div>
        </div>
      ))}
    </div>
  )
}

export { noticesProxy, Notices, showNotice, type Notice }
